import { useLocation, useNavigate } from "react-router-dom";

import { Icon } from "@/components/icons";
import { useI18n } from "@/i18n/useI18n";
import { useUIStore, useUserStore } from "@/store";
import { navItems, NavItem } from "@/components/layout/constants";
import { hasAllRequiredPermissions } from "@/constants";

export function Sidebar() {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarCollapsed, expandedMenuItems, toggleSidebar, toggleMenuItem } =
    useUIStore();
  const userPermissions = useUserStore((state) => state.currentUser?.permissions ?? []);

  const isPathActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const isItemActive = (item: NavItem): boolean => {
    if (item.path && isPathActive(item.path)) return true;
    if (item.subItems) {
      return item.subItems.some((sub) => isPathActive(sub.path));
    }
    return false;
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tLabel = (key: string) => t(key as any);

  return (
    <aside
      className={`relative flex h-full min-h-0 shrink-0 flex-col bg-background transition-all duration-300 ${sidebarCollapsed ? "w-[72px]" : "w-[200px]"
        }`}
    >
      <button
        onClick={toggleSidebar}
        className="absolute -right-3 top-10 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-md transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground cursor-pointer"
        title={sidebarCollapsed ? t("layout.expandMenu") : t("layout.collapseMenu")}
      >
        {sidebarCollapsed ? (
          <Icon icon="chevronsRight" className="h-3 w-3" />
        ) : (
          <Icon icon="chevronsLeft" className="h-3 w-3" />
        )}
      </button>

      {/* Agency Header */}
      <div
        className={`flex items-center px-4 py-4 text-secondary ${sidebarCollapsed ? "justify-center" : "justify-between"
          }`}
      >
        <div className="flex items-center gap-2">
          <Icon icon="store" className="h-5 w-5" />
          {!sidebarCollapsed && (
            <span className="text-sm font-medium font-semibold">
              {t("layout.agency")}
            </span>
          )}
        </div>
        {!sidebarCollapsed && (
          <button>
            <Icon icon="moreHorizontal" className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto pl-3 py-4 border-t border-sidebar-border">
        <ul className="space-y-1">
          {navItems.map((item) => {
            const visibleSubItems = item.subItems?.filter((subItem) =>
              hasAllRequiredPermissions(userPermissions, subItem.requiredPermissions),
            );
            if (item.subItems && visibleSubItems?.length === 0) {
              return null;
            }
            const itemHasAccess =
              !item.requiredPermissions ||
              hasAllRequiredPermissions(userPermissions, item.requiredPermissions);
            if (item.subItems && !itemHasAccess) {
              return null;
            }
            const visibleItem = visibleSubItems ? { ...item, subItems: visibleSubItems } : item;
            const hasSubItems = visibleItem.subItems && visibleItem.subItems.length > 0;
            const isExpanded = expandedMenuItems.includes(visibleItem.id);
            const isActive = isItemActive(visibleItem);
            const isParentActive = isActive && hasSubItems;

            const statusClass = (() => {
              if (isParentActive) {
                return "bg-primary text-primary-foreground";
              }
              if (isActive && !hasSubItems) {
                return "bg-primary text-primary-foreground";
              }
              return "text-muted-foreground hover:bg-sidebar-accent";
            })();

            return (
              <li key={visibleItem.id}>
                <button
                  onClick={() => {
                    if (hasSubItems) {
                      if (sidebarCollapsed) {
                        toggleSidebar();
                      }
                        toggleMenuItem(visibleItem.id);
                    } else if (visibleItem.path) {
                      handleNavigate(visibleItem.path);
                    }
                  }}
                  className={`flex w-full items-center rounded-md px-3 py-2.5 text-sm transition-colors cursor-pointer ${sidebarCollapsed ? "justify-center" : "justify-between"
                    } ${statusClass}`}
                  title={sidebarCollapsed ? tLabel(visibleItem.labelKey) : undefined}
                >
                  <div
                    className={`flex items-center ${sidebarCollapsed ? "" : "gap-3"
                      }`}
                  >
                    <span>
                      <Icon icon={visibleItem.icon} className="h-5 w-5" />
                    </span>
                    {!sidebarCollapsed && <span>{tLabel(visibleItem.labelKey)}</span>}
                  </div>
                  {hasSubItems && !sidebarCollapsed && (
                    <span>
                      {isExpanded ? (
                        <Icon icon="chevronUp" className="h-4 w-4" />
                      ) : (
                        <Icon icon="chevronDown" className="h-4 w-4" />
                      )}
                    </span>
                  )}
                </button>

                {/* Sub Items with left border indicator */}
                {hasSubItems && isExpanded && !sidebarCollapsed && (
                  <ul className="ml-4 pl-2 mt-1 space-y-1 border-l-1 border-border">
                    {visibleItem.subItems!.map((subItem) => (
                      <li key={subItem.id}>
                        <button
                          onClick={() => handleNavigate(subItem.path)}
                          className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors cursor-pointer ${isPathActive(subItem.path)
                              ? "text-primary bg-accent"
                              : "text-muted-foreground hover:bg-accent"
                            }`}
                        >
                          <Icon icon={subItem.icon} className="h-4 w-4" />
                          {tLabel(subItem.labelKey)}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="border-t border-sidebar-border px-4 py-4">
        {sidebarCollapsed ? (
          <div className="flex flex-col items-center gap-2">
            <Icon
              icon="mail"
              className="h-4 w-4 text-muted-foreground"
              title={t("layout.contact.email1")}
            />
            <Icon
              icon="phone"
              className="h-4 w-4 text-muted-foreground"
              title={t("layout.contact.phone1")}
            />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Icon icon="mail" className="h-4 w-4" />
              <span>{t("layout.contact.email2")}</span>
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <Icon icon="phone" className="h-4 w-4" />
              <span>{t("layout.contact.phone2")}</span>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              {t("layout.copyright")}
            </p>
          </>
        )}
      </div>
    </aside>
  );
}
